import React, { useMemo } from 'react';
import useGameStore from '../../stores/gameStore';

export interface KeyedListEntry {
  id: string;
  label: string;
}

export interface KeyedListProps {
  sourceVariable: string;
  selectionVariable?: string;
  onEntrySelect?: (entryId: string) => void;
  className?: string;
  style?: React.CSSProperties;
  customColor?: string;
}

const getColorWithAlpha = (color: string, alpha: string) => {
  if (color.startsWith('#')) {
    return `${color}${alpha}`;
  }
  return color;
};

function parseKeyedListEntries(raw: string): KeyedListEntry[] {
  if (!raw.trim()) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(
      (entry): entry is KeyedListEntry =>
        Boolean(entry) &&
        typeof entry === 'object' &&
        typeof (entry as KeyedListEntry).id === 'string' &&
        typeof (entry as KeyedListEntry).label === 'string'
    );
  } catch {
    return [];
  }
}

export const KeyedList: React.FC<KeyedListProps> = ({
  sourceVariable,
  onEntrySelect,
  className = '',
  style,
  customColor
}) => {
  const rawValue = useGameStore(
    useMemo(
      () => (state) => String(state.userVariableValues[sourceVariable]?.value ?? ''),
      [sourceVariable]
    )
  );

  const entries = useMemo(() => parseKeyedListEntries(rawValue), [rawValue]);

  if (entries.length === 0) {
    return (
      <div
        className={`keyed-list empty ${className}`}
        style={style}
        data-testid="keyed-list-empty"
      >
        <div className="flex items-center justify-center py-2">
          <p className="italic text-center text-gray-400">No entries</p>
        </div>
      </div>
    );
  }

  if (!onEntrySelect) {
    return (
      <div className={`keyed-list ${className}`} style={style} data-testid="keyed-list">
        <div className="flex flex-col" style={{ gap: '0.5rem' }}>
          {entries.map((entry) => (
            <div
              key={entry.id}
              className="keyed-list-item-card"
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '0.75rem',
                padding: '0.75rem 1rem'
              }}
              data-testid={`keyed-list-card-${entry.id}`}
            >
              <span className="text-base text-white">{entry.label}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`keyed-list selectable ${className}`}
      style={style}
      data-testid="keyed-list-selectable"
    >
      <div className="keyed-list-items flex flex-col" style={{ gap: '0.5rem' }}>
        {entries.map((entry) => {
          const buttonStyle: React.CSSProperties = {};
          if (customColor) {
            buttonStyle.border = `1px solid ${getColorWithAlpha(customColor, '40')}`;
            buttonStyle.background = `linear-gradient(135deg, ${getColorWithAlpha(customColor, '88')}, ${getColorWithAlpha(customColor, 'AA')})`;
          }

          return (
            <button
              key={entry.id}
              type="button"
              onClick={() => onEntrySelect(entry.id)}
              className="keyed-list-item-button inventory-item-button w-full"
              style={buttonStyle}
              data-testid={`keyed-list-item-${entry.id}`}
              data-entry-id={entry.id}
              aria-label={`Select ${entry.label}`}
            >
              <div className="flex items-center w-full" style={{ padding: '0.75rem 1rem' }}>
                <span className="text-base text-white">{entry.label}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default KeyedList;
